// Skoda Integration Configuration
// Maps Skoda OIDC response fields to application user fields

// The Skoda organization ID in Firebase - configured per environment in .env files
export const SKODA_ORGANIZATION_ID = import.meta.env.VITE_SKODA_ORGANIZATION_ID || "";

// Field mappings from Skoda OIDC profile to our user model
export const skodaProfileMapping = {
  // Primary identifier - use this instead of email since users can have multiple emails
  uniqueIdentifier: "preferred_username", // or "sub" or "unique_identifier"

  // Standard user fields
  email: "email",
  firstName: "given_name",
  lastName: "family_name",
  displayName: "name",

  // Used as display fallback in UI
  preferredUsername: "preferred_username",

  // Used to validate Skoda org membership
  organization: "organization",
};

// Expected organization name in the OIDC response to confirm it's a Skoda user
export const SKODA_ORGANIZATION_NAME = "skoda";

/**
 * Extracts relevant user data from Skoda OIDC profile
 * @param {Object} profile - The OIDC profile from Firebase Auth (getAdditionalUserInfo)
 * @param {Object} tokenClaims - Claims from the raw OIDC ID token that Firebase strips (azp, roles)
 * @returns {Object} - Mapped user data for our application
 */
export const mapSkodaProfileToUserData = (profile, tokenClaims = {}) => {
  if (!profile) return null;

  return {
    // Primary identifier - used for DB lookup
    skodaIdentifier: profile[skodaProfileMapping.uniqueIdentifier] || profile.sub,

    // Standard user fields
    email: profile.email || "",
    firstName: profile.given_name || "",
    lastName: profile.family_name || "",
    displayName: profile.name || profile.display_name || "",
    preferredUsername: profile.preferred_username || "",

    // From decoded ID token (Firebase strips these from the profile)
    roles: tokenClaims.roles || [],
    isSkodaUser: profile.organization.toLowerCase().includes(SKODA_ORGANIZATION_NAME.toLowerCase()),
  };
};

/**
 * Validates that the profile is from a valid Skoda user
 * @param {Object} profile - The OIDC profile
 * @returns {boolean}
 */
export const isValidSkodaProfile = (profile) => {
  if (!profile) return false;

  // Must have a preferred_username or sub
  const hasIdentifier = Boolean(profile.preferred_username || profile.sub);

  // Must be from Skoda organization
  const isFromSkoda = profile.organization.toLowerCase().includes(SKODA_ORGANIZATION_NAME.toLowerCase());

  return hasIdentifier && isFromSkoda;
};

export default {
  SKODA_ORGANIZATION_ID,
  SKODA_ORGANIZATION_NAME,
  skodaProfileMapping,
  mapSkodaProfileToUserData,
  isValidSkodaProfile,
};
